import React from 'react';
import { Card } from '@/lib/components/ui/card';
import { Badge } from '@/lib/components/ui/badge';
import { Button } from '@/lib/components/ui/button';
import { Eye, Edit, Trash2, Shield, Lock, Save } from 'lucide-react';
import { TAbac } from '@/data/types';

interface PermissionsViewProps {
  currentSelections: TAbac;
  onActionChange: (action: 'READ' | 'UPDATE' | 'DELETE' | undefined) => void;
  onPrivateChange: (isPrivate: boolean) => void;
  onSavePermissions: (name: string, description?: string) => void;
}

export const PermissionsView: React.FC<PermissionsViewProps> = ({
  currentSelections,
  onActionChange,
  onPrivateChange,
  onSavePermissions,
}) => {
  const handleActionSelect = (action: 'READ' | 'UPDATE' | 'DELETE') => {
    const newAction = currentSelections.action === action ? undefined : action;
    onActionChange(newAction);
  };

  const getPermissionLevel = (action: 'READ' | 'UPDATE' | 'DELETE') => {
    switch (action) {
      case 'READ':
        return 1;
      case 'UPDATE':
        return 2;
      case 'DELETE':
        return 3;
      default:
        return 0;
    }
  };

  const isPermissionIncluded = (action: 'READ' | 'UPDATE' | 'DELETE') => {
    if (!currentSelections.action) return false;
    return getPermissionLevel(action) <= getPermissionLevel(currentSelections.action);
  };

  const hasSelections =
    (currentSelections.roles || [])?.length > 0 ||
    (currentSelections.week_days || [])?.length > 0 ||
    (currentSelections.month_days || [])?.length > 0 ||
    (currentSelections.hours || [])?.length > 0;

  return (
    <div className="bg-gradient-to-br from-gray-50/30 via-white to-blue-50/20 p-8">
      <div className="animate-fade-in mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Set Permissions</h2>

        {/* Subtle Selection Tags */}
        {hasSelections && (
          <div className="animate-fade-in animation-delay-100 mt-4 flex flex-wrap gap-2">
            {currentSelections.roles && currentSelections.roles.length > 0 && (
              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                Roles ({currentSelections.roles.length})
              </Badge>
            )}
            {currentSelections.week_days && currentSelections.week_days.length > 0 && (
              <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
                Week Days ({currentSelections.week_days.length})
              </Badge>
            )}
            {currentSelections.month_days && currentSelections.month_days.length > 0 && (
              <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                Month Days ({currentSelections.month_days.length})
              </Badge>
            )}
            {currentSelections.hours && currentSelections.hours.length > 0 && (
              <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700">
                Time Ranges ({currentSelections.hours.length})
              </Badge>
            )}
          </div>
        )}
      </div>

      {!hasSelections ? (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <Shield className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No attributes selected</h3>
            <p className="mt-2 text-sm text-gray-600">Go back and select some attributes to configure permissions.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Permission Actions */}
          <div className="space-y-4">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Choose Permission Level</h3>
              <p className="mt-2 text-gray-600">Higher permissions automatically include all lower-level permissions</p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {(['READ', 'UPDATE', 'DELETE'] as const).map((action, index) => {
                const isSelected = currentSelections.action === action;
                const isIncluded = isPermissionIncluded(action);
                const actionConfig = {
                  READ: {
                    icon: Eye,
                    title: 'Read',
                    subtitle: 'View and access content',
                    includes: [],
                    selectedClass: 'border-blue-500/50 bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-blue-100',
                    includedClass: 'border-blue-400/40 bg-gradient-to-br from-blue-50/60 to-blue-100/40 shadow-blue-50',
                    hoverClass: 'hover:border-blue-300 hover:bg-blue-50/30',
                    iconColor: 'text-blue-600',
                    iconBg: 'bg-blue-100',
                  },
                  UPDATE: {
                    icon: Edit,
                    title: 'Update',
                    subtitle: 'Modify and edit content',
                    includes: ['Read'],
                    selectedClass:
                      'border-yellow-500/50 bg-gradient-to-br from-yellow-50 to-yellow-100/50 shadow-yellow-100',
                    includedClass:
                      'border-yellow-400/40 bg-gradient-to-br from-yellow-50/60 to-yellow-100/40 shadow-yellow-50',
                    hoverClass: 'hover:border-yellow-300 hover:bg-yellow-50/30',
                    iconColor: 'text-yellow-600',
                    iconBg: 'bg-yellow-100',
                  },
                  DELETE: {
                    icon: Trash2,
                    title: 'Delete',
                    subtitle: 'Remove content permanently',
                    includes: ['Read', 'Update'],
                    selectedClass: 'border-red-500/50 bg-gradient-to-br from-red-50 to-red-100/50 shadow-red-100',
                    includedClass: 'border-red-400/40 bg-gradient-to-br from-red-50/60 to-red-100/40 shadow-red-50',
                    hoverClass: 'hover:border-red-300 hover:bg-red-50/30',
                    iconColor: 'text-red-600',
                    iconBg: 'bg-red-100',
                  },
                };

                const config = actionConfig[action];
                const Icon = config.icon;

                return (
                  <Card
                    key={action}
                    className={`group animate-fade-in relative cursor-pointer overflow-hidden border-2 bg-white/60 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 ${
                      isSelected
                        ? `scale-[1.02] transform ${config.selectedClass} shadow-lg`
                        : isIncluded && !isSelected
                          ? `scale-[1.01] transform ${config.includedClass} shadow-md`
                          : `border-gray-200/50 ${config.hoverClass}`
                    }`}
                    style={{ animationDelay: `${index * 150}ms` }}
                    onClick={() => handleActionSelect(action)}
                  >
                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full ${config.iconBg} shadow-sm`}
                        >
                          <div className={`h-2 w-2 rounded-full ${config.iconColor.replace('text-', 'bg-')}`}></div>
                        </div>
                      </div>
                    )}

                    {/* Included Indicator */}
                    {isIncluded && !isSelected && (
                      <div className="absolute top-3 right-3">
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full ${config.iconBg} shadow-sm`}
                        >
                          <div className={`h-2 w-2 rounded-full ${config.iconColor.replace('text-', 'bg-')}`}></div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-14 w-14 items-center justify-center rounded-xl transition-all duration-300 ${
                          isSelected
                            ? `${config.iconBg} shadow-lg`
                            : isIncluded && !isSelected
                              ? `${config.iconBg} shadow-md`
                              : 'bg-gray-100 group-hover:bg-white group-hover:shadow-md'
                        }`}
                      >
                        <Icon
                          className={`h-7 w-7 transition-colors ${
                            isSelected
                              ? config.iconColor
                              : isIncluded && !isSelected
                                ? config.iconColor
                                : 'text-gray-400 group-hover:text-gray-600'
                          }`}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3
                            className={`text-lg font-semibold transition-colors ${
                              isSelected
                                ? 'text-gray-900'
                                : isIncluded && !isSelected
                                  ? 'text-gray-900'
                                  : 'text-gray-700 group-hover:text-gray-900'
                            }`}
                          >
                            {config.title}
                          </h3>
                          {isIncluded && !isSelected && (
                            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">included</span>
                          )}
                        </div>
                        <p
                          className={`mt-1 text-sm leading-relaxed transition-colors ${
                            isSelected
                              ? config.iconColor
                              : isIncluded && !isSelected
                                ? config.iconColor
                                : 'text-gray-500 group-hover:text-gray-600'
                          }`}
                        >
                          {config.subtitle}
                        </p>
                        {config.includes.length > 0 && (
                          <p className="mt-2 text-xs text-gray-400">Includes: {config.includes.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
